import React, { useState, useRef, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';

const TerminalView = ({ 
  terminalTabs, 
  activeTerminalTab, 
  setActiveTerminalTab, 
  setTerminalTabs,
  agentStatus,
  terminalRef 
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const activeTerminalIndex = terminalTabs.findIndex(tab => tab.id === activeTerminalTab);
  
  const activeTerminalContent = useMemo(() => {
    const activeTab = terminalTabs.find(tab => tab.id === activeTerminalTab);
    if (!activeTab) return '';
    return activeTab.content || '';
  }, [terminalTabs, activeTerminalTab]);

  const handleTerminalSelect = (tabId) => {
    setActiveTerminalTab(tabId);
    setDropdownOpen(false);
  };
  
  const handleTerminalClose = (e, tabId) => {
    e.stopPropagation();
    if (agentStatus !== 'generating') {
      setTerminalTabs(tabs => tabs.filter(t => t.id !== tabId));
      if (activeTerminalTab === tabId) {
        const remaining = terminalTabs.filter(t => t.id !== tabId);
        setActiveTerminalTab(remaining.length > 0 ? remaining[0].id : null);
      }
    }
  };

  return (
    <div className="terminal-view h-full flex flex-col bg-slate-100 dark:bg-neutral-900 overflow-hidden">
      <div className="bg-slate-300 dark:bg-neutral-800 flex items-center justify-between px-3 py-1.5">
        <div className="relative" ref={dropdownRef}>
          <button
            className="flex items-center space-x-1 bg-slate-300 dark:bg-neutral-700 hover:bg-slate-400 dark:hover:bg-neutral-600 rounded px-2 py-1 text-ui-text-primary text-xs shadow-sm"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span>
              {activeTerminalIndex >= 0 
                ? `Terminal ${activeTerminalIndex + 1}` 
                : 'Select Terminal'
              }
            </span>
            <ChevronDown size={12} />
          </button>
          
          {dropdownOpen && (
            <div className="absolute left-0 top-full mt-1 bg-slate-200 dark:bg-neutral-800 rounded shadow-lg z-50 max-h-60 overflow-y-auto dropdown-scrollbar w-44">
              {terminalTabs.length > 0 ? (
                <div>
                  {terminalTabs.map((tab, i) => (
                    <div
                      key={tab.id}
                      className={`flex items-center justify-between p-2 text-xs cursor-pointer ${activeTerminalTab === tab.id ? 'bg-slate-300 dark:bg-neutral-700 text-ui-text-primary dark:text-semantic-success font-medium' : 'text-ui-text-primary hover:bg-slate-300 dark:hover:bg-neutral-700'}`}
                      onClick={() => handleTerminalSelect(tab.id)}
                    >
                      <span>Terminal {i + 1}</span>
                      <button
                        className={`p-1 rounded hover:bg-slate-400 dark:hover:bg-neutral-600 ${agentStatus === 'generating' ? 'opacity-50 cursor-not-allowed' : 'hover:text-semantic-error'}`}
                        onClick={(e) => handleTerminalClose(e, tab.id)}
                        disabled={agentStatus === 'generating'}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-2 text-xs text-ui-text-tertiary">No terminals available</div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          <span className="text-xs text-ui-text-tertiary">
            {terminalTabs.length} {terminalTabs.length === 1 ? 'terminal' : 'terminals'}
          </span>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto custom-scrollbar" ref={terminalRef}>
        <pre className="p-3 m-0 font-mono text-xs text-ui-text-primary whitespace-pre-wrap min-h-full bg-slate-100 dark:bg-neutral-900">
          {activeTerminalContent || 'No output yet...'}
        </pre>
      </div>
    </div>
  );
};

export default TerminalView; 